/**
 * A tiny semaphore: `limit(task)` runs `task` once fewer than `max` tasks
 * are running, and the rest wait their turn in order. A settled task hands
 * its slot straight to the next one waiting.
 */
export const createLimiter = (
  max: number
): (<T>(task: () => Promise<T>) => Promise<T>) => {
  let active = 0
  const waiting: (() => void)[] = []

  const acquire = (): Promise<void> => {
    if (active < max) {
      active += 1
      return Promise.resolve()
    }
    return new Promise((resolve) => waiting.push(resolve))
  }

  const release = (): void => {
    const next = waiting.shift()
    if (next) next()
    else active -= 1
  }

  return async <T>(task: () => Promise<T>): Promise<T> => {
    await acquire()
    try {
      return await task()
    } finally {
      release()
    }
  }
}

/**
 * Shared by every members read of the bulk dialog, so a folder of hundreds of
 * projects (and the refetch after a run) never fires them all at once.
 */
export const limitMembersRead = createLimiter(6)
